import { Injectable, signal, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, finalize, shareReplay, tap, catchError, of, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AccessRequest, AccessResponse } from '../models/api.models';

const TOKEN_KEY = 'brinks_auth_token';
const LOGIN_KEY = 'brinks_auth_login';

/** Marks GET /access session probe so the interceptor does not recurse. */
export const SESSION_CHECK_HEADER = 'X-Brinks-Session-Check';

/** One-time move of auth keys from sessionStorage → localStorage (tab-share). */
function migrateSessionToLocal(): void {
  for (const key of [TOKEN_KEY, LOGIN_KEY]) {
    const fromSession = sessionStorage.getItem(key);
    if (fromSession && !localStorage.getItem(key)) {
      localStorage.setItem(key, fromSession);
    }
    sessionStorage.removeItem(key);
  }
}

migrateSessionToLocal();

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly _isAuthenticated = signal(!!localStorage.getItem(TOKEN_KEY));
  readonly isAuthenticated = this._isAuthenticated.asReadonly();

  private readonly _currentLogin = signal<string | null>(localStorage.getItem(LOGIN_KEY));
  readonly currentLogin = this._currentLogin.asReadonly();

  /** Prevents parallel 401s from stacking navigations. */
  private handlingUnauthorized = false;

  /** Shared in-flight GET /access so many 401s only probe once. */
  private sessionCheck$: Observable<boolean> | null = null;

  constructor() {
    // Keep auth signals in sync when another tab logs in/out.
    window.addEventListener('storage', (event) => {
      if (event.key !== TOKEN_KEY && event.key !== LOGIN_KEY) return;
      const token = localStorage.getItem(TOKEN_KEY);
      this._isAuthenticated.set(!!token);
      this._currentLogin.set(localStorage.getItem(LOGIN_KEY));
      if (!token && this.router.url !== '/login') {
        void this.router.navigate(['/login'], { replaceUrl: true });
      }
    });
  }

  login(login: string, password: string): Observable<AccessResponse> {
    const body: AccessRequest = { login, password };
    return this.http.post<AccessResponse>(`${environment.apiUrl}/access`, body).pipe(
      tap((res) => {
        localStorage.setItem(TOKEN_KEY, res.token);
        localStorage.setItem(LOGIN_KEY, login);
        this._isAuthenticated.set(true);
        this._currentLogin.set(login);
        this.handlingUnauthorized = false;
      }),
    );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(LOGIN_KEY);
    this._isAuthenticated.set(false);
    this._currentLogin.set(null);
  }

  /**
   * Probe current session via GET /access.
   * @returns `true` if token is still valid, `false` if unauthorized / no token.
   */
  checkSession(): Observable<boolean> {
    const token = this.getToken();
    if (!token) {
      return of(false);
    }

    if (!this.sessionCheck$) {
      this.sessionCheck$ = this.http
        .get(`${environment.apiUrl}/access`, {
          headers: { [SESSION_CHECK_HEADER]: '1' },
        })
        .pipe(
          map(() => true),
          catchError((err: unknown) => {
            if (err instanceof HttpErrorResponse && err.status === 401) {
              return of(false);
            }
            // Network / 5xx — do not treat as logged-out.
            return of(true);
          }),
          finalize(() => {
            this.sessionCheck$ = null;
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    return this.sessionCheck$;
  }

  /**
   * After an API 401: verify session with GET /access; logout only if that is also 401.
   */
  handleUnauthorized(): void {
    if (this.handlingUnauthorized) return;
    this.handlingUnauthorized = true;

    this.checkSession()
      .pipe(
        finalize(() => {
          this.handlingUnauthorized = false;
        }),
      )
      .subscribe((valid) => {
        if (valid) return;
        this.logout();
        void this.router.navigate(['/login'], {
          queryParams: { sessionExpired: '1' },
          replaceUrl: true,
        });
      });
  }

  /** Force logout without session probe (e.g. GET /access itself returned 401). */
  forceUnauthorizedLogout(): void {
    if (this.handlingUnauthorized) return;
    this.handlingUnauthorized = true;
    this.logout();
    void this.router
      .navigate(['/login'], {
        queryParams: { sessionExpired: '1' },
        replaceUrl: true,
      })
      .finally(() => {
        this.handlingUnauthorized = false;
      });
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }
}
